import { motion } from 'motion/react';
import { Sparkles } from 'lucide-react';
import type { MajorStory } from '@/services/api';
import type { StockAnalogLensData } from '@/mocks/stockAnalogMock';
import { MajorStoryDateCards } from '@/components/MajorStoryDateCards';

function directionBadgeClass(direction: string | undefined): string {
  switch (direction?.toLowerCase()) {
    case 'bullish':
      return 'bg-emerald-50 text-emerald-800';
    case 'bearish':
      return 'bg-rose-50 text-rose-800';
    default:
      return 'bg-amber-50 text-amber-800';
  }
}

type Props = {
  story: MajorStory;
  weekLabel?: string | null;
};

export function MajorStoryDetail({ story, weekLabel }: Props) {
  const lens = story.lens as StockAnalogLensData | undefined;
  const matches = lens?.matches ?? [];
  const aiSummary = lens?.aiSummary ?? story.summary;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-3xl"
    >
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {story.direction ? (
          <span
            className={`rounded-lg px-2.5 py-0.5 text-xs capitalize ${directionBadgeClass(story.direction)}`}
          >
            {story.direction}
          </span>
        ) : null}
        {weekLabel ? <span className="text-sm text-gray-500">{weekLabel}</span> : null}
      </div>

      <h1 className="mb-6 text-3xl tracking-tight text-gray-900">{story.title}</h1>

      {aiSummary ? (
        <div className="mb-8 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5">
          <div className="mb-2 flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-emerald-800" />
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
              AI summary
            </p>
          </div>
          <p className="text-sm leading-relaxed text-gray-700">{aiSummary}</p>
        </div>
      ) : null}

      {matches.length > 0 ? (
        <MajorStoryDateCards matches={matches} chartSymbol={lens?.chartSymbol} />
      ) : story.body ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="space-y-4 text-sm leading-relaxed text-gray-700">
            {story.body.split('\n\n').map((paragraph) => (
              <p key={paragraph.slice(0, 48)}>{paragraph}</p>
            ))}
          </div>
        </div>
      ) : null}
    </motion.div>
  );
}
